import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { officeAPI } from '../../services/api';
import { useOfficeAuth } from '../../context/OfficeAuthContext';

export default function OfficeDashboardPage() {
  const { session } = useOfficeAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    officeAPI.getDashboard()
      .then((res) => setStats(res.data.data))
      .catch(() => toast.error('خطأ في تحميل البيانات'))
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString('ar-SD', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div dir="rtl" className="p-4 md:p-6">
      {/* Welcome */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          مرحباً، {session?.user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-sm text-gray-500 mt-1">{today}</p>
        <p className="text-sm font-medium text-blue-600 mt-0.5">{session?.office?.name} — {session?.office?.city}</p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">جاري التحميل...</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">حجوزات اليوم</p>
            <p className="text-3xl font-bold text-blue-700 mt-2">{stats?.totalBookings ?? 0}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">مقاعد باعها المكتب</p>
            <p className="text-3xl font-bold text-indigo-700 mt-2">{stats?.totalSeats ?? 0}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">إجمالي المبيعات</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{(stats?.totalRevenue ?? 0).toLocaleString()}</p>
            <p className="text-xs text-gray-400">ج.س</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">عمولة المكتب</p>
            <p className="text-2xl font-bold text-emerald-700 mt-2">{(stats?.totalCommission ?? 0).toLocaleString()}</p>
            <p className="text-xs text-gray-400">ج.س</p>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">إجراءات سريعة</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/office/new-booking" className="bg-blue-600 text-white rounded-2xl p-5 flex items-center gap-4 hover:bg-blue-700 transition shadow-sm">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-base">حجز جديد</p>
            <p className="text-xs text-blue-200 mt-0.5">إصدار تذكرة لمسافر</p>
          </div>
        </Link>

        <Link to="/office/bookings" className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-base">الحجوزات</p>
            <p className="text-xs text-gray-500 mt-0.5">عرض وإدارة حجوزات المكتب</p>
          </div>
        </Link>

        <Link to="/office/commission" className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition">
          <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-base">العمولات</p>
            <p className="text-xs text-gray-500 mt-0.5">ملخص أرباح المكتب</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
